import { soundManager } from '../../app/SoundManager';
import type { App } from '../../app/App';
import { startDioramaFlow } from './startDioramaFlow';
import type { GameSceneOptions } from '../../game/scene/GameSceneOptions';
import { startNewLevel } from '../startNewLevel';
import { exitToMainMenu } from '../exitToMainMenu';
import type { LevelData } from '../../game/level/LevelData';
import type { LevelTasksTracker } from '../../game/tasks/LevelTasksTracker';
import { PIGGY_BANK_TIER_CONFIG_MAP } from '../../gameEvents/piggyBank/PiggyBankConstants';
import type { GameSceneView } from '../../game/views/GameSceneView';
import { MultiplatformHandler } from '../../app/multiplatform/MultiplatformHandler';
import type { AppStateController } from '../../app/state/AppStateController';
import type { MetaProperties } from '../../app/tracking/trackingModules/avo/Avo';
import { AdResponse } from '../../app/tracking/trackingModules/avo/Avo';
import { NewWinPopup } from '../../game/views/popups/winFlow/NewWinPopup';
import { openRateUsPopupForLevel } from '../openRateUsPopup';
import { addSeasonPassTokens } from '../../gameEvents/seasonPass/addSeasonPassTokens';
import { createSeasonPassDependencies } from '../../gameEvents/adapters/createSeasonPassDependencies';
import type { PiggyBankState } from '../../gameEvents/piggyBank/PiggyBankState';
import type { PiggyBankTier } from '../../gameEvents/piggyBank/types';

interface StartWinFlowConfig {
  options: GameSceneOptions;
  sceneView: GameSceneView;
  taskTracker: LevelTasksTracker;
  metaProperties?: MetaProperties[];
}

function computePiggyBankReward(
  state: AppStateController,
  initialPiggyBank: PiggyBankState,
): { tier: PiggyBankTier, initBalance: number; balance: number; maxCoins: number; reward: number } | null {
  const currentPiggyBank = state.getPiggyBankState();

  return {
    tier: currentPiggyBank.currentTier,
    initBalance: initialPiggyBank.coins,
    balance: currentPiggyBank.coins,
    maxCoins: currentPiggyBank.maxCoins,
    reward: PIGGY_BANK_TIER_CONFIG_MAP[currentPiggyBank.currentTier].coinsPerLevel,
  };
}

export async function startWinFlow(app: App, config: StartWinFlowConfig): Promise<void> {
  const { options, sceneView, taskTracker: tasks } = config;

  const { resources, controller, stateController: state, tournamentService, ads } = app;

  const { saveProgress, levelData } = options;

  const levelNumber = state.getLevelNumber();

  const coinCounter = sceneView.getTopBarView().getCoinCounter();

  const initialPiggyBank = state.getPiggyBankState();

  addSeasonPassTokens(createSeasonPassDependencies(app), 1);

  if (saveProgress) {
    if (coinCounter) coinCounter.updateEnabled = false;
    resources.addLevelWinReward(levelData.reward);
    trackLevelComplete(app, levelData, config.metaProperties);
  }

  app.gameEvents.onLevelWon();

  const nextLevelData = saveProgress ? state.setNextLevel() : options.levelData;

  await ads?.showInterstitialAd('win');

  soundManager.play(`UI_Win_Screen`);

  const popup = new NewWinPopup({
    level: levelNumber,
    reward: levelData.reward,
    piggyBank: computePiggyBankReward(state, initialPiggyBank),
    dioramaReward: tasks.getDioramaFillAmount(),
    doubleRewardAvailable: !!ads && levelData.reward > 0 && levelNumber >= 2,
    coinCounter,
  });

  popup.onNextClicked.connect(async () => {
    popup.root.interactiveChildren = false;

    await popup.playCollectRewardAnimation();

    await controller.popups.closeActivePopup();

    if (saveProgress) {
      void sceneView.hideUI();
      MultiplatformHandler.gameOver(state.getLevelIndex(), tasks.getCombinedTally());

      // Always check for Rate Us popup after specific levels, regardless of diorama flow
      // Rate Us will handle internal logic to avoid showing multiple times
      await openRateUsPopupForLevel(app, levelNumber);

      if (state.canAddProgressToDiorama() && tasks.getDioramaFillAmount() > 0) {
        void startDioramaFlow(app, tasks.getDioramaFillAmount());
        void tournamentService?.increaseScore(tasks.getDioramaFillAmount(), true);
      } else {
        app.stateController.resetLevel();
        exitToMainMenu(app);
      }
    } else {
      startNewLevel(app, { ...options, levelData: nextLevelData });
    }
  });

  let doubleCoinsUsed = false;

  popup.onDoubleCoinsClicked.connect(async () => {
    if (doubleCoinsUsed) return;
    doubleCoinsUsed = true;
    const response = await ads?.showRewardedAd('double-reward');
    if (response === AdResponse.SUCCESS) {
      resources.addLevelWinDoubledReward(levelData.reward);
      void popup.animateDoubleReward(levelData.reward * 2);
    } else {
      doubleCoinsUsed = false;
    }
  });

  sceneView.fadeInWinScreenBackground();

  void controller.popups.addPopup({
    popupInstance: popup,
  });
}

function trackLevelComplete(app: App, levelData: LevelData, metaProperties?: MetaProperties[]): void {
  const { stateController, tracking, resources } = app;
  const levelNumber = stateController.getLevelNumber();
  const doubleRewardAvailable = levelData.reward > 0 && levelNumber >= 2;

  tracking.trackLevelWon({
    ...stateController.getLevelTrackingData(),
    doubleRewardShown: doubleRewardAvailable,
    inventory: resources.getInventory(),
    metaProperties,
  });
}
